import React, { useContext, useEffect, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
  TablePagination
} from "@mui/material"
import { useLogger, useGlobalLoader, UserContext } from "src/providers"
import { RequestError } from "src/models/errors"
import moment from "moment"
import { getMultilangJobs, MultilangJob, MultilangJobFilters, MultilangJobSortBy, displayedName } from "src/services/api/document"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import JobButton from "./JobButton"
import { StatusChip } from "../../components/table/StatusChip"
import SortableTableHeaderCell, { Order } from "../../components/table/SortableTableHeaderCell"
import TableHeaderText from "../../components/table/TableHeaderText"
import { useTranslation } from "react-i18next"
import { CardColor } from "src/components/browser/card/BrowserCard"
import { useInterval } from "src/utils/useInterval"
import { RequesterRecord } from "./MyFilesFilter"
import { getRequesterName } from "./MyFilesFilter"
import { appBarHeight } from "src/providers/AppProviders"
import { useResponsive } from "src/utils/useResponsive"

interface MyFilesTableProps {
  setMultilangJob: (multilangJob: MultilangJob) => void
  filters: MultilangJobFilters
  requesterList: RequesterRecord[] | undefined
  onOpenEmailModal: () => void
  updatedJob?: MultilangJob
}

const MyFilesTable: React.FC<MyFilesTableProps> = ({ setMultilangJob, filters, requesterList, onOpenEmailModal, updatedJob }) => {
  const { isMobile } = useResponsive()
  const { user } = useContext(UserContext)
  const { showLoader, hideLoader } = useGlobalLoader()
  const [jobs, setJobs] = useState<MultilangJob[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [order, setOrder] = useState<Order>("desc")
  const [orderBy, setOrderBy] = useState<MultilangJobSortBy>("uploadDate")
  const { t } = useTranslation("common")
  useEffect(() => {
    setPage(0)
  }, [filters])

  const { logger } = useLogger()

  const handleError = (e: any) => {
    if (e instanceof RequestError && e.status !== 403) {
      logger.error("System - Failed to fetch jobs", e)
    }
  }

  const fetchJobs = (ignoreLoader = false) => {
    if (!ignoreLoader) {
      showLoader()
    }
    getMultilangJobs({
      sortBy: orderBy,
      sortDirection: order,
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      ...filters
    })
      .then(({ totalCount, results }) => {
        setTotalCount(totalCount)
        setJobs(results)
      })
      .catch(handleError)
      .finally(() => {
        if (!ignoreLoader) {
          hideLoader()
        }
      })
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    fetchJobs()
  }, [filters, page, rowsPerPage, order, orderBy])
  useInterval(() => fetchJobs(true), 10000)

  useEffect(() => {
    if (!updatedJob) return
    setJobs((prev) =>
      prev.map((job) => (job.id === updatedJob.id ? { ...job, externalProjectId: updatedJob.externalProjectId } : job))
    )
  }, [updatedJob])

  const handleRequestSort = (property: MultilangJobSortBy) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
    setPage(0)
  }

  return (
    <Stack
      sx={{
        ...(isMobile ? { width: "100vw" } : undefined),
        flex: 1,
        minHeight: 0,
        overflow: "hidden"
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          flex: 1,
          minHeight: 0,
          overflow: "auto"
        }}
      >
        <Table sx={{ minWidth: { md: 650 } }} aria-label="my files table" size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <SortableTableHeaderCell<MultilangJobSortBy>
                label="Project name"
                orderBy={orderBy}
                order={order}
                property="name"
                onRequestSort={handleRequestSort}
                leftPadding
              />
              <SortableTableHeaderCell<MultilangJobSortBy>
                label="Status"
                orderBy={orderBy}
                order={order}
                property="status"
                onRequestSort={handleRequestSort}
              />
              <SortableTableHeaderCell<MultilangJobSortBy>
                label="Upload date"
                orderBy={orderBy}
                order={order}
                property="uploadDate"
                onRequestSort={handleRequestSort}
              />
              {user && user.company.profile === "ORGANIZATION_ADMIN" && requesterList && (
                <TableCell>
                  <TableHeaderText>Requester</TableHeaderText>
                </TableCell>
              )}
              <TableCell>
                <TableHeaderText>Actions</TableHeaderText>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell sx={{ pl: { xs: 2, md: 6 } }}>{displayedName(job)}</TableCell>
                <TableCell>
                  <StatusChip label={t(`files.${job.status}`).toUpperCase()} size="small" color={getColorForJob(job)} />
                </TableCell>
                <TableCell>{moment(job.uploadDate).format("MM/DD/YYYY, HH:mm")}</TableCell>
                {user && user.company.profile === "ORGANIZATION_ADMIN" && requesterList && (
                  <TableCell>{getRequesterName(requesterList, job.requesterId)}</TableCell>
                )}
                <TableCell sx={{ py: 0 }}>
                  <Stack direction="row" spacing={1.25} justifyContent="flex-start" alignItems="center">
                    <IconButton color={getColorForJob(job)} sx={{ p: 0.75 }} onClick={() => setMultilangJob(job)}>
                      <InfoOutlinedIcon />
                    </IconButton>
                    <JobButton job={job} showDownloadButton={true} onModalClose={fetchJobs} iconOnly={isMobile} onOpenEmailModal={onOpenEmailModal} />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Stack>
  )
}

export default MyFilesTable

function getColorForJob(job: MultilangJob): CardColor {
  if (["FINISHED"].includes(job.status)) return "electricMint"
  if (["CANCELLED"].includes(job.status)) return "gray"
  if (["REJECTED"].includes(job.status)) return "pastelRed"
  if (["QUOTED", "QUOTING"].includes(job.status)) return "lightGold"
  return "primary"
}
